import { checkDriverConfig, isBuiltInDriverName } from '@casa/drivers/config';

// A connection's configuration as a form (dashboard-editor spec C-2, C-3): the fields of a driver's
// `configSchema` (JSON Schema), each typed, required or not, and secret where the schema says so.
// Secrets are never shown again: the form keeps the stored value unless the user types a new one.

export interface ConfigField {
    readonly name: string;
    /** From the field's name: `appId` is "App id", `refreshInterval` "Refresh interval". */
    readonly label: string;
    readonly description?: string;
    /** `number` also for a field that takes a number or its digits (Hubitat's `appId`); `choice` for one of a list (`enum`). */
    readonly kind: 'text' | 'number' | 'secret' | 'choice';
    readonly isRequired: boolean;
    /** A choice's values, named by the schema's `x-labels` where it names them. */
    readonly choices?: readonly { readonly value: string; readonly label: string }[];
}

function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isNumberType = (schema: unknown) : boolean => isRecord(schema) && (schema.type === 'number' || schema.type === 'integer');

function labelOf(name: string) : string {
    const words = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();

    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The fields of a configuration schema, in its order. */
export function configFields(schema: unknown) : ConfigField[] {
    if (!isRecord(schema) || !isRecord(schema.properties)) {
        return [];
    }

    const required = new Set(Array.isArray(schema.required) ? schema.required.filter((name): name is string => typeof name === 'string') : []);

    return Object.entries(schema.properties).flatMap(([name, property]) => {
        if (!isRecord(property)) {
            return [];
        }

        const numeric = isNumberType(property) || (Array.isArray(property.anyOf) && property.anyOf.some(isNumberType));
        const values = Array.isArray(property.enum) && property.enum.every((value) => typeof value === 'string') ? (property.enum as string[]) : undefined;
        const labels = isRecord(property['x-labels']) ? property['x-labels'] : {};
        const kind = property.secret === true ? 'secret' : values ? 'choice' : numeric ? 'number' : 'text';

        return [{
            name,
            label: typeof property.title === 'string' ? property.title : labelOf(name),
            ...(typeof property.description === 'string' ? { description: property.description } : {}),
            kind,
            isRequired: required.has(name),
            ...(values && kind === 'choice' ? { choices: values.map((value) => ({ value, label: typeof labels[value] === 'string' ? labels[value] : value })) } : {}),
        } satisfies ConfigField];
    });
}

/** What a field shows: the stored value as text; a secret shows nothing (C-3). */
export function shownValue(field: ConfigField, value: unknown) : string {
    if (field.kind === 'secret' || value === undefined || value === null) {
        return '';
    }

    return String(value);
}

/**
 * The configuration from what was typed, over the stored one: an empty secret keeps the stored
 * secret, an empty field is left out, a number field holds a number where the text is one.
 */
export function configFrom(fields: readonly ConfigField[], typed: Readonly<Record<string, string>>, stored: Readonly<Record<string, unknown>> = {}) : Record<string, unknown> {
    const config: Record<string, unknown> = {};
    // What the form does not show (a key the schema does not list) stays as it was.
    for (const [name, value] of Object.entries(stored)) {
        if (!fields.some((field) => field.name === name)) {
            config[name] = value;
        }
    }
    for (const field of fields) {
        const text = (typed[field.name] ?? '').trim();
        if (field.kind === 'secret' && text === '') {
            if (stored[field.name] !== undefined) {
                config[field.name] = stored[field.name];
            }
            continue;
        }
        if (text === '') {
            continue;
        }

        config[field.name] = field.kind === 'number' && Number.isFinite(Number(text)) ? Number(text) : text;
    }

    return config;
}

/** Problems with a configuration, in words; built-in drivers are checked against their schema. */
export function configProblems(driver: string, fields: readonly ConfigField[], config: Readonly<Record<string, unknown>>) : string[] {
    const missing = fields.filter((field) => field.isRequired && config[field.name] === undefined).map((field) => `${field.label} is needed`);
    if (missing.length > 0 || !isBuiltInDriverName(driver)) {
        return missing;
    }

    return checkDriverConfig(driver, config);
}

/** The address a connection reaches, for the list (C-1): the first field its driver may connect to. */
export function connectionAddress(configHosts: readonly string[], config: Readonly<Record<string, unknown>>) : string | undefined {
    for (const key of configHosts) {
        const value = config[key];
        if (typeof value === 'string' && value !== '') {
            return value;
        }
    }

    return undefined;
}
